/**
 * Deterministic name -> colour mapping for badges (Account, College, etc).
 * Same name always gets the same colour across the app; new values added via
 * SM Options automatically get a colour with zero code changes — no
 * per-name hardcoded list.
 */
const PALETTE = [
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
  { bg: 'bg-lime-100', text: 'text-lime-700', border: 'border-lime-200' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
];

const NEUTRAL = { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

/** Returns { bg, text, border } Tailwind classes for a given name. */
export const getBadgeColor = (name) => {
  if (!name || name === 'No College' || name === 'NA') return NEUTRAL;
  return PALETTE[hashString(name) % PALETTE.length];
};

const hexToRgb = (hex) => {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

/**
 * Turns an admin-configured hex colour into a light-tinted, readable badge
 * style: soft background (12% alpha), darkened text (65% of full value so it
 * stays legible on the light tint), subtle border (35% alpha). Never renders
 * the raw hex as a solid dark background.
 */
export const hexToBadgeStyle = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { r, g, b } = rgb;
  const dr = Math.round(r * 0.65);
  const dg = Math.round(g * 0.65);
  const db = Math.round(b * 0.65);
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
    color: `rgb(${dr}, ${dg}, ${db})`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.35)`,
  };
};

/**
 * Single entry point for badge rendering: prefers the admin-configured hex
 * colour (from SM Options, passed in as {name: hex}), falls back to the
 * deterministic hash palette above when unconfigured — so a badge always
 * renders something reasonable and never breaks.
 * Returns { style, className } — spread both onto the badge element.
 */
export const resolveBadgeStyle = (name, colorMap = {}) => {
  const hex = name && colorMap[name];
  const configured = hex && hexToBadgeStyle(hex);
  if (configured) return { style: configured, className: 'border' };
  const c = getBadgeColor(name);
  return { style: undefined, className: `${c.bg} ${c.text} ${c.border} border` };
};

export default getBadgeColor;
